/**
 * Search navigation helpers.
 * Gold coin listings are served outside the React app and require a full page refresh.
 */

import { IMAGE_SEARCH_PAGE_PATH } from '../constants';
import { markSearchTriggered } from './searchSession';

const GOLD_COIN_PATH_REGEX = /\/goldcoin|^\/gold[+%2B]coins/i;

export function isGoldCoinSearchQuery(query: string): boolean {
  const trimmed = query.trim();
  if (!trimmed) return false;

  const pathOnly = trimmed.split('?')[0];
  if (GOLD_COIN_PATH_REGEX.test(pathOnly)) return true;

  const normalized = trimmed
    .toLowerCase()
    .replace(/[^a-z0-9+]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!normalized) return false;

  const compact = normalized.replace(/[\s+]/g, '');

  if (/goldcoins?/.test(compact)) return true;
  if (normalized === 'coin' || normalized === 'coins') return true;
  if (/\bgold\b/.test(normalized) && /\bcoins?\b/.test(normalized)) return true;

  return false;
}

export function isSilverCoinSearchQuery(query: string): boolean {
  if (!query?.trim()) return false;
  const normalizedQuery = query.toLowerCase();
  return normalizedQuery.includes('silver') &&
    (
      normalizedQuery.includes('coin') ||
      normalizedQuery.includes('bar')
    );
}

export function buildSearchPageUrl(query: string): string {
  const trimmed = query.trim();
  const params = new URLSearchParams({
    orderby: 'position',
    search_category: 'regular',
    search_type: 'search',
    _eventId: 'searched_for_product',
    orderway: 'desc',
    search_query: trimmed,
  });
  return `/search?${params.toString()}`;
}

/** Image search results PLP — same-origin relative path (keeps sessionStorage). */
export function buildImageSearchPageUrl(): string {
  return IMAGE_SEARCH_PAGE_PATH;
}

type ClientNavigateFn = (to: string, options?: { state?: unknown }) => void;

/** Client-side navigation after image upload (no full page reload). */
export function navigateToImageSearchPage(navigate: ClientNavigateFn): void {
  navigate(buildImageSearchPageUrl(), {
    state: { fromImageSearch: true, ts: Date.now() },
  });
}

type NavigateToSearchFn = (url: string) => void;

export function navigateToSearchPage(query: string, navigate: NavigateToSearchFn): void {
  const trimmed = query.trim();
  if (!trimmed) return;

  markSearchTriggered();
  const searchUrl = buildSearchPageUrl(trimmed);
  if (isGoldCoinSearchQuery(trimmed)) {
    window.location.href = searchUrl;
    return;
  }
  navigate(searchUrl);
}
